/**
 * Apartsales — TopHeader.
 *
 * Layout:
 *   [👤 photo]          apartsales          [🛍 bag]
 *   Паспорт              wordmark            Корзина
 *
 * Photo opens /passport, bag opens /cart, wordmark opens / (Главная).
 *
 * Receives `scrolled` and `hidden` from AppShell which owns the scroll
 * listener. Pure presentational.
 */

import { Link } from '@tanstack/react-router';
import { BRAND_SVG, BRAND_ASPECT } from '@apartsales/brand';
import { BagIcon, HeartIcon } from './icons';
import { cn } from '~/lib/cn';
import { useCartCount } from '~/lib/useCart';

interface TopHeaderProps {
  scrolled: boolean;
  hidden: boolean;
}

export function TopHeader({ scrolled, hidden: _hidden }: TopHeaderProps) {
  const cartCount = useCartCount();
  return (
    <header
      className={cn(
        'relative z-30 h-12 shrink-0 transition-colors duration-[220ms]',
        scrolled ? 'backdrop-blur-xl backdrop-saturate-150' : '',
      )}
      style={{
        transitionTimingFunction: 'cubic-bezier(0.2, 0.8, 0.2, 1)',
        // Непрозрачная подложка var(--color-bg) под полупрозрачным
        // header-blur: фон не зависит от backdrop-filter, не просвечивает.
        background: scrolled
          ? 'linear-gradient(var(--color-header-blur), var(--color-header-blur)), var(--color-bg)'
          : 'var(--color-bg)',
      }}
    >
      <div className="grid h-full grid-cols-[1fr_auto_1fr] items-center px-3">
        {/* Left: Корзина */}
        <div className="flex justify-start">
          <Link
            to="/cart"
            aria-label={cartCount > 0 ? `Корзина — ${cartCount}` : 'Корзина'}
            className="relative grid h-10 w-10 place-items-center rounded-full text-label transition-colors hover:bg-[var(--color-hover)] active:bg-[var(--color-active)]"
          >
            <BagIcon size={26} />
            {cartCount > 0 && (
              <span
                aria-hidden
                className="absolute right-2 top-2 block h-2 w-2 rounded-full bg-[#FF453A] ring-2 ring-bg"
              />
            )}
          </Link>
        </div>

        {/* Center: apartsales wordmark — theme-aware. Both variants in DOM,
            CSS hides one via [data-theme] selector. Zero-flicker on theme swap. */}
        <Link
          to="/"
          aria-label="Apartsales — главная"
          className="flex items-center justify-center px-2"
        >
          <span
            className="block dark-logo-only"
            style={{
              width: 130,
              aspectRatio: BRAND_ASPECT['wordmark-white'],
            }}
            dangerouslySetInnerHTML={{
              __html: BRAND_SVG['wordmark-white']
                .replace(/\swidth="\d+"/, ' width="100%"')
                .replace(/\sheight="\d+"/, ' height="100%"'),
            }}
          />
          <span
            className="block light-logo-only"
            style={{
              width: 130,
              aspectRatio: BRAND_ASPECT['wordmark-black'],
            }}
            dangerouslySetInnerHTML={{
              __html: BRAND_SVG['wordmark-black']
                .replace(/\swidth="\d+"/, ' width="100%"')
                .replace(/\sheight="\d+"/, ' height="100%"'),
            }}
          />
        </Link>

        {/* Right: Избранное */}
        <div className="flex items-center justify-end gap-0.5">
          <Link
            to="/favorites"
            aria-label="Избранное"
            className="grid h-10 w-10 place-items-center rounded-full text-label transition-colors hover:bg-[var(--color-hover)] active:bg-[var(--color-active)]"
          >
            <HeartIcon size={24} />
          </Link>
        </div>
      </div>
    </header>
  );
}
